import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  ArrowUp,
  ArrowDown,
  Check,
  Globe,
  Smartphone,
  Palette,
  Search,
  Megaphone,
  Code2,
  Server,
  Cloud,
  ShieldCheck,
  Wifi,
} from "lucide-react";
import icons from "./data/icons";
import api from "./api/api";

const ICONS = {
  Globe,
  Smartphone,
  Palette,
  Search,
  Megaphone,
  Code2,
  Server,
  Cloud,
  ShieldCheck,
  Wifi,
};

const emptyForm = {
  title: "",
  description: "",
  category: "",
  subbadge: "",
  subtitle: "",
  organizedBy: "",
  duration: "",
  features: [""],
  summaryTitle: "",
  summaryValue: "",
  summaryDescription: "",
  link: "",
  icon: "Globe",
  color: "indigo",
  order: 0,
  isActive: true,
};
const colors = [
  { name: "indigo", className: "text-indigo-400 bg-indigo-500/10" },
  { name: "purple", className: "text-purple-400 bg-purple-500/10" },
  { name: "cyan", className: "text-cyan-400 bg-cyan-500/10" },
  { name: "emerald", className: "text-emerald-400 bg-emerald-500/10" },
  { name: "amber", className: "text-amber-400 bg-amber-500/10" },
  { name: "rose", className: "text-rose-400 bg-rose-500/10" },
];

const colorClasses = {
  indigo: "text-indigo-400 bg-indigo-500/10",
  purple: "text-purple-400 bg-purple-500/10",
  cyan: "text-cyan-400 bg-cyan-500/10",
  emerald: "text-emerald-400 bg-emerald-500/10",
  amber: "text-amber-400 bg-amber-500/10",
  rose: "text-rose-400 bg-rose-500/10",
};

const ServiceAdmin = ({ notify }) => {
  const [services, setServices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [movingId, setMovingId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState(emptyForm);

  // =========================================================
  // ICON SEARCH
  // =========================================================
  const [iconSearch, setIconSearch] = useState("");
  const [showIconSuggestions, setShowIconSuggestions] = useState(false);

  const iconSearchRef = useRef(null);

  // =========================================================
  // LOAD SERVICES
  // =========================================================
  const fetchServices = async () => {
    try {
      setLoading(true);

      const res = await api.get("/services");

      const raw = res?.data?.data ?? res?.data;

      const list = Array.isArray(raw)
        ? raw
        : raw?.services ?? raw?.items ?? [];

      setServices(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load services:", error);

      setServices([]);

      notify?.(
        "error",
        error?.response?.data?.message || "Failed to load services"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================
  // CLOSE ICON DROPDOWN WHEN CLICKING OUTSIDE
  // =========================================================
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        iconSearchRef.current &&
        !iconSearchRef.current.contains(event.target)
      ) {
        setShowIconSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // =========================================================
  // FORM FIELD
  // =========================================================
  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // =========================================================
  // FEATURE HANDLERS
  // =========================================================
  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const updateFeature = (index, value) => {
    setFormData((prev) => {
      const next = [...prev.features];
      next[index] = value;
      return { ...prev, features: next };
    });
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // =========================================================
  // CREATE
  // =========================================================
  const openCreateModal = () => {
    setModalMode("create");
    setEditingId(null);
    setFormData({ ...emptyForm, features: [""] });

    setIconSearch("");
    setShowIconSuggestions(false);

    setShowModal(true);
  };

  // =========================================================
  // EDIT
  // =========================================================
  const openEditModal = (service) => {
    setModalMode("edit");
    setEditingId(service.id);

    const selectedIcon = service.icon || "Globe";

    // Normalize features into array of strings
    const normalizedFeatures = Array.isArray(service.features)
      ? service.features.map((f) =>
        typeof f === "object" ? f.text || "" : String(f || "")
      )
      : [""];

    setFormData({
      title: service.title || "",
      description: service.description || "",
      category: service.category || "",
      subbadge: service.subbadge || "",
      subtitle: service.subtitle || service.Subtitle || "",
      organizedBy: service.organizedBy || "",
      duration: service.duration || "",
      features: normalizedFeatures.length ? normalizedFeatures : [""],
      summaryTitle: service.summary?.title || "",
      summaryValue: service.summary?.value || "",
      summaryDescription: service.summary?.description || "",
      link: service.link || "",
      icon: selectedIcon,
      color: service.color || "indigo",
      isActive: service.isActive !== false,
    });

    setIconSearch(selectedIcon);
    setShowIconSuggestions(false);

    setShowModal(true);
  };

  // =========================================================
  // CLOSE
  // =========================================================
  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditingId(null);
    setFormData({ ...emptyForm, features: [""] });

    setIconSearch("");
    setShowIconSuggestions(false);
  };

  // =========================================================
  // SELECT ICON
  // =========================================================
  const selectIcon = (iconName) => {
    setField("icon", iconName);
    setIconSearch(iconName);
    setShowIconSuggestions(false);
  };

  // =========================================================
  // FILTER ICONS
  // =========================================================
  const filteredIcons = Object.entries(ICONS).filter(([iconName]) =>
    iconName.toLowerCase().includes(iconSearch.trim().toLowerCase())
  );

  // =========================================================
  // SAVE
  // =========================================================
  const handleSave = async () => {
    if (saving) return;

    if (!formData.title.trim()) {
      notify?.("error", "Service title is required");
      return;
    }

    if (!formData.description.trim()) {
      notify?.("error", "Service description is required");
      return;
    }

    setSaving(true);

    try {
      // Clean features: remove empty entries
      const cleanedFeatures = formData.features
        .map((f) => f.trim())
        .filter(Boolean);

      // Build summary only if any field is filled
      const hasSummary =
        formData.summaryTitle.trim() ||
        formData.summaryValue.trim() ||
        formData.summaryDescription.trim();

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),

        // IMPORTANT: send empty strings instead of undefined
        category: formData.category.trim(),
        subbadge: formData.subbadge.trim(),
        subtitle: formData.subtitle.trim(),
        organizedBy: formData.organizedBy.trim(),
        duration: formData.duration.trim(),

        features: cleanedFeatures,

        // IMPORTANT: send null when summary is completely cleared
        summary: hasSummary
          ? {
            title: formData.summaryTitle.trim(),
            value: formData.summaryValue.trim(),
            description: formData.summaryDescription.trim(),
          }
          : null,

        // IMPORTANT: send empty string when link is cleared
        link: formData.link.trim(),

        icon: formData.icon,
        color: formData.color,
        order: formData.order,
        isActive: formData.isActive,
      };

      if (modalMode === "edit") {
        await api.put(`/services/${editingId}`, payload);
        notify?.("success", "Service updated successfully");
      } else {
        await api.post("/services", payload);
        notify?.("success", "Service created successfully");
      }

      closeModal();
      await fetchServices();
    } catch (error) {
      console.error("Failed to save service:", error);

      notify?.(
        "error",
        error?.response?.data?.message || "Failed to save service"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================
  const handleDelete = async (id) => {
    if (deletingId) return;

    const confirmed = window.confirm(
      "Delete this service? This action cannot be undone."
    );

    if (!confirmed) return;

    setDeletingId(id);

    try {
      await api.delete(`/services/${id}`);
      notify?.("success", "Service deleted successfully");
      await fetchServices();
    } catch (error) {
      console.error("Failed to delete service:", error);
      notify?.(
        "error",
        error?.response?.data?.message || "Failed to delete service"
      );
    } finally {
      setDeletingId(null);
    }
  };

  // =========================================================
  // MOVE UP
  // =========================================================
  const handleMoveUp = async (service, index) => {
    if (index === 0 || movingId) return;

    setMovingId(service.id);

    try {
      const previous = services[index - 1];

      await api.put(`/services/${service.id}`, { order: previous.order });
      await api.put(`/services/${previous.id}`, { order: service.order });

      await fetchServices();
    } catch (error) {
      console.error("Failed to move service:", error);
      notify?.("error", "Failed to move service");
    } finally {
      setMovingId(null);
    }
  };

  // =========================================================
  // MOVE DOWN
  // =========================================================
  const handleMoveDown = async (service, index) => {
    if (index === services.length - 1 || movingId) return;

    setMovingId(service.id);

    try {
      const next = services[index + 1];

      await api.put(`/services/${service.id}`, { order: next.order });
      await api.put(`/services/${next.id}`, { order: service.order });

      await fetchServices();
    } catch (error) {
      console.error("Failed to move service:", error);
      notify?.("error", "Failed to move service");
    } finally {
      setMovingId(null);
    }
  };

  // =========================================================
  // ICON
  // =========================================================
  const getIcon = (name) => ICONS[name] || Globe;

  // =========================================================
  // INPUT
  // =========================================================
  const inputClass = `
    w-full
    rounded-lg
    border
    border-zinc-700
    bg-zinc-800
    px-3
    py-2.5
    text-sm
    text-white
    outline-none
    placeholder:text-zinc-500
    focus:border-indigo-500
  `;

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Services</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage the services shown on your website.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="
            flex items-center gap-2
            bg-indigo-600 hover:bg-indigo-500
            px-4 py-2 rounded-lg font-medium transition
          "
        >
          <Plus size={17} />
          Add Service
        </button>
      </div>

      {/* LOADING */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-400">
          <Loader2 className="animate-spin mr-2" size={22} />
          Loading services...
        </div>
      ) : !Array.isArray(services) || services.length === 0 ? (
        <div
          className="
            text-center py-24 text-zinc-500
            border border-dashed border-zinc-800 rounded-xl
          "
        >
          No services yet.
          <br />
          Click "Add Service" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {services.map((service, index) => {
            const Icon = getIcon(service.icon);
            const featureCount = Array.isArray(service.features)
              ? service.features.length
              : 0;

            return (
              <div
                key={service.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-5"
              >
                {/* TOP */}
                <div className="flex items-start justify-between">
                  <div
                    className={`
                      w-11 h-11 rounded-lg flex items-center justify-center
                      ${colorClasses[service.color] || colorClasses.indigo}
                    `}
                  >
                    <Icon size={22} />
                  </div>

                  <span
                    className={`
                      text-[10px] px-2 py-1 rounded-full
                      ${service.isActive
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-zinc-700 text-zinc-400"
                      }
                    `}
                  >
                    {service.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* BADGES */}
                {(service.category || service.subbadge) && (
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    {service.category && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-500/90 border border-orange-600 text-[10px] text-white font-semibold uppercase">
                        {service.category}
                      </span>
                    )}
                    {service.subbadge && (
                      <span className="px-2 py-0.5 text-[10px] rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium">
                        {service.subbadge}
                      </span>
                    )}
                  </div>
                )}

                {/* CONTENT */}
                <h2 className="mt-4 text-lg font-semibold text-white">
                  {service.title}
                </h2>

                {(service.subtitle || service.Subtitle) && (
                  <p className="mt-1 text-xs text-zinc-500 font-medium">
                    {service.subtitle || service.Subtitle}
                  </p>
                )}

                {service.organizedBy && (
                  <p className="mt-1 text-xs text-zinc-400">
                    <span className="text-blue-500">Provided by: </span>
                    {service.organizedBy}
                  </p>
                )}

                {service.duration && (
                  <p className="mt-1 text-xs text-orange-400">
                    {service.duration}
                  </p>
                )}

                <p className="mt-2 text-sm text-zinc-400 leading-6 line-clamp-3">
                  {service.description}
                </p>

                {/* META ROW */}
                {(featureCount > 0 || service.summary?.value || service.link) && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {featureCount > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                        {featureCount} feature{featureCount > 1 ? "s" : ""}
                      </span>
                    )}
                    {service.summary?.value && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                        {service.summary.title || "Metric"}: {service.summary.value}
                      </span>
                    )}
                    {service.link && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                        Has link
                      </span>
                    )}
                  </div>
                )}

                {/* ACTIONS */}
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-800">
                  {/* ORDER */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleMoveUp(service, index)}
                      disabled={index === 0 || movingId === service.id}
                      className="p-2 rounded-lg hover:bg-zinc-800 disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp size={16} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleMoveDown(service, index)}
                      disabled={
                        index === services.length - 1 ||
                        movingId === service.id
                      }
                      className="p-2 rounded-lg hover:bg-zinc-800 disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>

                  {/* EDIT DELETE */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(service)}
                      className="p-2 rounded-lg text-indigo-400 hover:bg-indigo-500/10"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(service.id)}
                      disabled={deletingId === service.id}
                      className="
                        p-2 rounded-lg text-red-400
                        hover:bg-red-500/10 disabled:opacity-40
                      "
                      title="Delete"
                    >
                      {deletingId === service.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =====================================================
          MODAL
      ===================================================== */}
      {showModal && (
        <div
          className="
            fixed inset-0 z-50 bg-black/70 backdrop-blur-sm
            flex items-center justify-center p-4
          "
        >
          <div
            className="
              w-full max-w-2xl max-h-[90vh] flex flex-col
              bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl
            "
          >
            {/* MODAL HEADER */}
            <div
              className="
                flex items-center justify-between
                px-6 py-4 border-b border-zinc-800
              "
            >
              <h2 className="text-lg font-semibold">
                {modalMode === "edit" ? "Edit Service" : "Add Service"}
              </h2>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="p-2 rounded-lg hover:bg-zinc-800 disabled:opacity-40"
              >
                <X size={18} />
              </button>
            </div>

            {/* FORM (SCROLLABLE) */}
            <div className="p-6 space-y-5 overflow-y-auto">
              {/* TITLE */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Service Title *
                </label>
                <input
                  value={formData.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Web Development"
                  className={inputClass}
                />
              </div>

              {/* CATEGORY + SUBBADGE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Category
                  </label>
                  <input
                    value={formData.category}
                    onChange={(e) => setField("category", e.target.value)}
                    placeholder="Service"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Sub-badge
                  </label>
                  <input
                    value={formData.subbadge}
                    onChange={(e) => setField("subbadge", e.target.value)}
                    placeholder="New / Featured"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* SUBTITLE + ORGANIZED BY */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Subtitle
                  </label>
                  <input
                    value={formData.subtitle}
                    onChange={(e) => setField("subtitle", e.target.value)}
                    placeholder="Short tagline"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Provided By
                  </label>
                  <input
                    value={formData.organizedBy}
                    onChange={(e) => setField("organizedBy", e.target.value)}
                    placeholder="Team / Provider"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* DURATION */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Duration / Timeline
                </label>
                <input
                  value={formData.duration}
                  onChange={(e) => setField("duration", e.target.value)}
                  placeholder="e.g. 2 weeks • Ongoing"
                  className={inputClass}
                />
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Description *
                </label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Describe your service..."
                  className={inputClass}
                />
              </div>

              {/* =================================================
                  ICON AUTOCOMPLETE
              ================================================= */}
              <div ref={iconSearchRef} className="relative">
                <label className="block text-sm text-zinc-300 mb-2">
                  Icon
                </label>

                <div className="relative">
                  <div
                    className="
                      absolute left-3 top-1/2 -translate-y-1/2
                      pointer-events-none text-zinc-400
                    "
                  >
                    {(() => {
                      const SelectedIcon = getIcon(formData.icon);
                      return <SelectedIcon size={18} />;
                    })()}
                  </div>

                  <input
                    value={iconSearch}
                    onChange={(e) => {
                      setIconSearch(e.target.value);
                      setShowIconSuggestions(true);
                    }}
                    onFocus={() => setShowIconSuggestions(true)}
                    placeholder="Search icon..."
                    className="
                      w-full h-11 rounded-lg border border-zinc-700
                      bg-zinc-800 pl-10 pr-10 text-sm text-white
                      outline-none placeholder:text-zinc-500
                      focus:border-indigo-500
                    "
                  />

                  {iconSearch && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIconSearch("");
                        setField("icon", "");
                        setShowIconSuggestions(true);
                      }}
                      className="
                        absolute right-3 top-1/2 -translate-y-1/2
                        text-zinc-500 hover:text-white
                      "
                      title="Clear icon"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {showIconSuggestions && (
                  <div
                    className="
                      absolute left-0 right-0 top-full z-[100] mt-1
                      max-h-72 overflow-y-auto rounded-lg
                      border border-zinc-700 bg-[#050817] shadow-2xl
                    "
                  >
                    {filteredIcons.length > 0 ? (
                      filteredIcons.map(([iconName, Icon]) => {
                        const active = formData.icon === iconName;
                        return (
                          <button
                            key={iconName}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectIcon(iconName);
                            }}
                            className={`
                              flex w-full items-center gap-4 px-4 py-2.5
                              text-left transition
                              ${active
                                ? "bg-indigo-500/10 text-indigo-400"
                                : "text-zinc-300 hover:bg-zinc-800"
                              }
                            `}
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                              <Icon size={19} />
                            </span>
                            <span className="truncate text-sm">{iconName}</span>
                            {active && (
                              <Check
                                size={16}
                                className="ml-auto shrink-0 text-indigo-400"
                              />
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-4 text-center text-sm text-zinc-500">
                        No icons found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* COLOR */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Color
                </label>
                <div className="flex gap-2">
                  {colors.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setField("color", item.name)}
                      className={`
                        w-9 h-9 rounded-lg ${item.className} border
                        ${formData.color === item.name
                          ? "border-white"
                          : "border-transparent"
                        }
                      `}
                      title={item.name}
                    >
                      {formData.color === item.name && (
                        <Check size={15} className="mx-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* FEATURES */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-zinc-300">
                    Features
                  </label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="
                      flex items-center gap-1 text-xs text-indigo-400
                      hover:text-indigo-300
                    "
                  >
                    <Plus size={14} />
                    Add Feature
                  </button>
                </div>

                <div className="space-y-2">
                  {formData.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={feature}
                        onChange={(e) => updateFeature(i, e.target.value)}
                        placeholder={`Feature ${i + 1}`}
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(i)}
                        disabled={formData.features.length === 1}
                        className="
                          p-2 rounded-lg text-red-400
                          hover:bg-red-500/10 disabled:opacity-30
                        "
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SUMMARY */}
              <div className="border-t border-zinc-800 pt-5">
                <label className="block text-sm text-zinc-300 mb-3">
                  Summary / Metric{" "}
                  <span className="text-zinc-500 font-normal">(optional)</span>
                </label>

                <div className="space-y-3">
                  <input
                    value={formData.summaryTitle}
                    onChange={(e) => setField("summaryTitle", e.target.value)}
                    placeholder="Summary title (e.g. Impact)"
                    className={inputClass}
                  />

                  <input
                    value={formData.summaryValue}
                    onChange={(e) => setField("summaryValue", e.target.value)}
                    placeholder="Value (e.g. 99.9%)"
                    className={inputClass}
                  />

                  <textarea
                    rows={2}
                    value={formData.summaryDescription}
                    onChange={(e) =>
                      setField("summaryDescription", e.target.value)
                    }
                    placeholder="Short summary description..."
                    className={inputClass}
                  />
                </div>
              </div>

              {/* LINK */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  External Link{" "}
                  <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <input
                  value={formData.link}
                  onChange={(e) => setField("link", e.target.value)}
                  placeholder="https://example.com"
                  className={inputClass}
                />
              </div>

              {/* ACTIVE */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setField("isActive", e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-zinc-300">
                  Status:{" "}
                  <span
                    className={
                      formData.isActive
                        ? "text-emerald-400"
                        : "text-zinc-500"
                    }
                  >
                    {formData.isActive ? "Active" : "Inactive"}
                  </span>
                </span>
              </label>
            </div>

            {/* FOOTER */}
            <div
              className="
                flex justify-end gap-3 px-6 py-4
                border-t border-zinc-800
              "
            >
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="
                  flex items-center gap-2 px-5 py-2 rounded-lg
                  bg-indigo-600 hover:bg-indigo-500
                  disabled:opacity-50 text-sm font-medium
                "
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {modalMode === "edit" ? "Update Service" : "Create Service"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceAdmin;